import { faDownload, faPlus, faTrash, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Button, Col, CustomInput, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table, Tooltip } from "reactstrap";
import instance from "../../../axiosInstance";
import { useClaimFormContext } from "../../../context/administration/claimForm";
import { useProfileContext } from "../../../context/profile";
import useError from "../../../hooks/useError";
import { fileToBase64 } from "../../../utils/forms";
import JSZip from 'jszip';

const Attachment = ({ caseId }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modal, setModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const toggleImageModal = () => setModal(!modal);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    toggleImageModal();

  };


  const [validationErrors, setValidationErrors] = useState({
    attachmentTitle: false,
    attachment: false,
  });

  const { handleError } = useError();

  const { profile } = useProfileContext();
  const permissions = profile?.permission?.case;
  const [zipDownloadLoader, setZipDownloadLoader] = useState(false);

  const toggleModal = () => setModalIsOpen((pre) => !pre);

  const { attachment_description, setAttachmentDescription, attachment_file, setAttachmentFile, attachments, setAttachments } = useClaimFormContext();

  const addToAttachment = async () => {
    if (!attachment_description.trim() || attachment_file.length === 0) {
      return setValidationErrors({
        attachmentTitle: !attachment_description.trim(),
        attachment: attachment_file.length === 0,
      });
    }

    try {
      setIsAdding(true);

      const uploadedAttachments = [];

      // Upload files sequentially to prevent overwhelming the server with a large payload
      for (const file of attachment_file) {
        const base64Data = await fileToBase64(file);
        const payload = [{
          attachment_description: attachment_description,
          attachment_file_name: file.name,
          attachment_base64: base64Data,
        }];

        const { data } = await instance.post(`/case/addAttachment/${caseId}`, payload);

        const newItems = data.data.map((item) => ({
          ...item,
          file: file.name,
        }));
        
        uploadedAttachments.push(...newItems);
      }

      setAttachments((prevAttachments) => [
        ...prevAttachments,
        ...uploadedAttachments,
      ]);

      setAttachmentDescription("");
      setAttachmentFile([]);
    } catch (err) {
      handleError(err);
    } finally {
      setIsAdding(false);
    }
  };

  const deleteAttachment = async () => {
    try {
      setIsDeleting(true);
      await instance.delete(`/case/deleteAttachment/${deleteId}`);

      setAttachments(attachments.filter((attachment) => attachment.case_attachment_id !== deleteId));
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
      toggleModal();
    }
  };

  return (
    <Form>
      <h4>Attachments</h4>

      <Row>
        <Col md={4}>
          <FormGroup>
            <Label for="description">Description</Label>
            <Input
              type="textarea"
              autoComplete="off"
              rows="1"
              invalid={validationErrors.attachmentTitle}
              id="description"
              disabled={!permissions.addAttachment}
              value={attachment_description}
              onChange={(e) => {
                setAttachmentDescription(e.target.value);
                setValidationErrors((pre) => ({ ...pre, attachmentTitle: !e.target.value }));
              }}
            />
            <small>{validationErrors.attachmentTitle && <span className="text-danger">Description is required</span>}</small>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="file">Select Files</Label>
            <CustomInput
              type="file"
              id="contractForm"
              multiple
              disabled={!permissions.addAttachment}
              invalid={validationErrors.attachment}
              label={attachment_file.length > 0 ? `${attachment_file.length} files selected` : "Choose Files"}
              onChange={(e) => {
                setAttachmentFile([...e.target.files]);
                setValidationErrors((pre) => ({ ...pre, attachment: e.target.files.length === 0 }));
              }}
            />
            {validationErrors.attachment && <small className="text-danger">At least one attachment is required</small>}
          </FormGroup>
        </Col>
        <Col className={`d-flex justify-between align-self-end ${(validationErrors.attachment || validationErrors.attachmentTitle) && "pb-3"}`}>
          <Button color="primary" className="mb-3 mr-2" onClick={addToAttachment} disabled={isAdding || !permissions.addAttachment}>
            {isAdding && <Spinner size="sm" color="#fff" className="mr-2 mb-1" />}
            <FontAwesomeIcon icon={faPlus} />
          </Button>
          {attachments.length > 0 && (
            <Button loading={zipDownloadLoader} disabled={zipDownloadLoader} color="success" className="mb-3 ml-auto" onClick={async () => {
              try {
                const zip = new JSZip();
                setZipDownloadLoader(true);
                // Fetch all attachments and add them to zip
                const downloadPromises = attachments.map(async (attachment) => {
                  const response = await fetch(attachment.href);
                  const blob = await response.blob();
                  zip.file(attachment.file, blob);
                });

                await Promise.all(downloadPromises);

                // Generate and download zip
                const content = await zip.generateAsync({ type: "blob" });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(content);
                link.download = "attachments.zip";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
                setZipDownloadLoader(false);
              } catch (error) {
                handleError(error);
                setZipDownloadLoader(false);
              }
            }}>
              {zipDownloadLoader && <Spinner size="sm" color="#fff" className="mr-2 mb-0" style={{borderWidth:"0.12em", width:"0.8rem", height: "0.8rem"}} />}
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download All
            </Button>
          )}
        </Col>
      </Row>

      {attachments.length >= 0 && (
        <Table responsive striped className="mt-4">
          <thead>
            <tr>
              <th scope="col">Sr. No</th>
              <th scope="col">Description</th>
              <th scope="col">Attachment</th>
              <th scope="col">Created At</th>
              <th scope="col">Created By</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {attachments.map((attachment, index) => (
              <tr key={attachment.description}>
                <th scope="row">{index + 1}</th>
                <td>{attachment.description}</td>
                <td onClick={() => { attachment.file_type == "image" ? handleImageClick(attachment.href): window.open(attachment.href, "_blank") }} style={{ cursor: "pointer" }}>
                  {attachment.file}
                  {attachment.file_type == "image" && <img src={attachment.href}
                    alt="Preview"
                    style={{ width: "20px", height: "20px", objectFit: "cover", borderRadius: "50%" }}
                    className="ml-1"
                  />}
                </td>
                <td>{attachment.updated_at ? new Date(attachment.updated_at).toLocaleString() : "—"}</td>
                <td>{attachment.updated_by_name ?? "—"}</td>
                <td>
                  <a href={attachment.href} title={attachment.file} className="custom-link" download>
                    <FontAwesomeIcon className="cursor-pointer" icon={faDownload} />
                  </a>
                  {permissions?.deleteAttachment ? (
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="ml-2 cursor-pointer"
                      onClick={() => {
                        setDeleteId(attachment.case_attachment_id);
                        toggleModal();
                      }}
                    />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal isOpen={modalIsOpen} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Delete Confirmation</ModalHeader>
        <ModalBody className="m-3">
          <h4>Are you sure you want to delete this attachment?</h4>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" outline onClick={toggleModal}>
            Cancle
          </Button>
          <Button color="danger" onClick={deleteAttachment} disabled={isDeleting}>
            {isDeleting && <Spinner size="sm" color="#fff" className="mr-2 mb-1" />}
            <span>Delete</span>
          </Button>
        </ModalFooter>
      </Modal>
      <style>
        {`
      .image-custom-modal .modal-content{
        background: transparent !important;
      }
      .close-button {
        position: absolute;
        bottom: 20px;
        right: 50%;
        z-index: 1000;
        background: rgba(0, 0, 0, 0.5);
        border: none;
        color: white;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.3s;
      }
      .close-button:hover {
        background: rgba(0, 0, 0, 0.7);
      }
      `}
      </style>
      <Modal isOpen={modal} toggle={toggleImageModal} size="lg" className="modal-full image-custom-modal" style={{ background: "transparent" }}>
        <ModalBody className="p-0 d-flex align-items-center justify-content-center" style={{ height: '90vh', width: "100%", background: "transparent" }}>
        <button className="close-button" onClick={toggleImageModal}>
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </button>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Preview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: "transparent"
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </Form>
  );
};

export default Attachment;
